/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat, Priska.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import icon from 'flarum/common/helpers/icon';
import type Mithril from 'mithril';
import BookshelfSettingsMenu from './BookshelfSettingsMenu';
import BookshelfPrivacyDropdown from './BookshelfPrivacyDropdown';
import BookshelfArticleCount from './BookshelfArticleCount';
import isOwnBookshelf from '../helpers/isOwnBookshelf';
import BookshelfArticleStatusCounts from './BookshelfArticleStatusCounts';
import type Bookshelf from '../../common/models/Bookshelf';

export interface BookshelfHeroAttrs extends ComponentAttrs {
	bookshelf: Bookshelf | null;
}

export default class BookshelfHero<CustomAttrs extends BookshelfHeroAttrs = BookshelfHeroAttrs> extends Component<CustomAttrs> {
	view(): Mithril.Children {
		const { bookshelf } = this.attrs;

		return (
			<div className={`BookshelfHero Hero IndexPageHero ${!isOwnBookshelf(bookshelf) ? 'noControls' : ''}`}>
				<div className="container">
					<div className="containerNarrow">
						<div className="BookshelfHero-breadcrumbs">
							<BookshelfBreadcrumbs bookshelf={bookshelf} />
						</div>
						{bookshelf
							? [
									<div className="BookshelfHero-infos">
										<BookshelfArticleCount bookshelf={bookshelf} />
										<BookshelfPrivacyDropdown bookshelf={bookshelf} />
									</div>,
									<BookshelfArticleStatusCounts bookshelf={bookshelf} />,
								]
							: [
									<div className="BookshelfHero-infos BookshelfHero-infos_skeleton"></div>,
									<div className="BookshelfHero-infos BookshelfHero-infos_skeleton"></div>,
								]}
						{isOwnBookshelf(bookshelf) && (
							<div className="IndexPageHero-controls">
								<BookshelfSettingsMenu bookshelf={bookshelf} />
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
}

export interface BookshelfBreadcrumbsAttrs extends BookshelfHeroAttrs {}

export class BookshelfBreadcrumbs<CustomAttrs extends BookshelfBreadcrumbsAttrs = BookshelfBreadcrumbsAttrs> extends Component<CustomAttrs> {
	view(): Mithril.Children {
		const { bookshelf } = this.attrs;

		if (!bookshelf) {
			return (
				<div>
					<h1 className="Hero-title BookshelfHero-title_skeleton"></h1>
					<h1 className="Hero-title BookshelfHero-title_skeleton"></h1>
				</div>
			);
		}

		return (
			<h1 className="Hero-title">
				{icon('far fa-books')}
				<BookshelvesLink bookshelf={bookshelf} />
				{icon('far fa-chevron-right')}
				<span>{bookshelf.name()}</span>
			</h1>
		);
	}
}

export interface BookshelvesLinkAttrs extends BookshelfHeroAttrs {}

export class BookshelvesLink<CustomAttrs extends BookshelvesLinkAttrs = BookshelvesLinkAttrs> extends Component<CustomAttrs> {
	view(): Mithril.Children {
		const { bookshelf } = this.attrs;
		const user = bookshelf ? bookshelf.user() : false;

		if (!bookshelf || !user) {
			return <span></span>;
		}

		if (isOwnBookshelf(bookshelf)) {
			return <Link href={app.route.book_collections()}>{app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelves.own_title')}</Link>;
		}

		return (
			<Link
				href={app.route('user.book_collections', {
					username: user.username(),
				})}
			>
				{app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelves.user_bookshelves_title', {
					displayName: user.displayName(),
				})}
			</Link>
		);
	}
}
